import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { IApiResponse } from '@interfaces/api-response.interface';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';

import { IMenuItem, IMenuPermissions, IMenuSystemFilters } from './menu-system.interface';
import { environment } from 'environments/environment';
@Injectable({
  providedIn: 'root',
})
export class MenuSystemService {
  private http = inject(HttpClient);
  private fb = inject(FormBuilder);

  private homePage = signal<string | null>(null);

  setHomePage(v: string) {
    if (v) {
      this.homePage.set(v);
    }
  }

  getHomePage(): Signal<string | null> {
    return this.homePage.asReadonly();
  }

  clearHomePage() {
    this.homePage.set(null);
  }

  formFilterList = new FormGroup({
    system: new FormControl(''),
    environment: new FormControl(''),
    userType: new FormControl(''),
    page: new FormControl(0),
    size: new FormControl(10),
  });

  list(payload: IMenuSystemFilters): Observable<IApiResponse<IMenuItem>> {
    let params = new HttpParams()
      .set('page', payload.page)
      .set('size', payload.size);

    if (payload.id) {
      params = params.set('id', payload.id);
    }

    if (payload.system) {
      params = params.set('system', payload.system);
    }

    if (payload.environment) {
      params = params.set('environment', payload.environment);
    }

    if (payload.userType) {
      params = params.set('userType', payload.userType);
    }

    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
        'Access-Control-Allow-Origin': '*',
      }),
      params,
    };

    return this.http.get<IApiResponse<IMenuItem>>(
      `${environment.api}/menu-system/list-all`,
      http
    );
  }

  find(id: string): Observable<IMenuItem> {
    const http = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
        'Access-Control-Allow-Origin': '*',
      }),
    };

    return this.http.get<IMenuItem>(
      `${environment.api}/menu-system/${id}`,
      http
    );
  }

  register(payload: IMenuItem) {
    if (payload.id) {
      return this.update(payload);
    }
    return this.create(payload);
  }

  create(payload: IMenuItem) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
        'Content-Type': 'application/json',
      }),
    };

    const body = payload;

    return this.http.post<IMenuItem>(
      `${environment.api}/menu-system/create`,
      body,
      httpOptions
    );
  }

  update(payload: IMenuItem) {
    let params = new HttpParams();

    if (payload.id) {
      params = params.append('menuId', payload.id);
    }

    const body = payload;

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
        'Content-Type': 'application/json',
      }),
      params,
    };

    return this.http.put<IMenuItem>(
      `${environment.api}/menu-system/update`,
      body,
      httpOptions
    );
  }

  delete(id: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
        'Access-Control-Allow-Origin': '*',
      }),
      responseType: 'text' as 'json',
    };

    return this.http.delete(
      `${environment.api}/menu-system/${id}`,
      httpOptions
    );
  }

  deleteAll() {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: sessionStorage.getItem('token') as string,
        'Access-Control-Allow-Origin': '*',
      }),
      responseType: 'text' as 'json',
    };

    return this.http.delete(
      `${environment.api}/menu-system/delete-all`,
      httpOptions
    );
  }


  createMenuItem(menu?: IMenuPermissions): FormGroup {
    const controlsConfig = {
      code: [menu?.code || '', Validators.required],
      external: [menu?.external ?? false, Validators.required],
      icon: [menu?.icon || ''],
      legend: [menu?.legend || '', Validators.required],
      order: [menu?.order ?? 50, Validators.required],
      route: [menu?.route || '', Validators.required],
      checked: [menu?.checked ?? false],
      permissionType: [menu?.permissionType || ''],
      subRoute: this.fb.array([]),
      homePage: [menu?.homePage ?? false],
    };

    const subRoute = controlsConfig.subRoute as FormArray;

    if (menu?.subRoute?.length) {
      menu.subRoute.forEach(subMenu => {
        subRoute.push(this.createMenuItem(subMenu));
      });
    }

    const menuItem = this.fb.group(controlsConfig);

    const checkedControl = menuItem.get('checked');
    const permissionTypeControl = menuItem.get('permissionType');

    const updatePermissionTypeValidator = (checked: boolean | null | undefined) => {
      const isChecked = !!checked;

      if (isChecked) {
        permissionTypeControl?.setValidators([Validators.required]);
      } else {
        permissionTypeControl?.clearValidators();
        permissionTypeControl?.setValue('');
      }

      permissionTypeControl?.updateValueAndValidity();
    };

    updatePermissionTypeValidator(checkedControl?.value);

    checkedControl?.valueChanges.subscribe((checked: boolean | null) => {
      updatePermissionTypeValidator(checked);
    });

    menuItem.get('code')?.disable();
    menuItem.get('external')?.disable();
    menuItem.get('icon')?.disable();
    menuItem.get('route')?.disable();

    return menuItem;
  }
}
